import { useCallback, useEffect, useState } from "react";
import {
  createFaq,
  deleteFaq,
  fetchFaqs,
  regenerateFaqAudio,
  updateFaq,
  type FaqInput,
  type VoiceFaq,
} from "../voiceApi";

const CATEGORIES = [
  "hours", "pricing", "location", "reservations", "age",
  "food", "events", "general", "attire", "group_size",
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  hours: "Hours",
  pricing: "Pricing",
  location: "Location",
  reservations: "Reservations",
  age: "Age Requirements",
  food: "Food & Drinks",
  events: "Events",
  general: "General",
  attire: "Attire",
  group_size: "Group Size",
};

type Props = { isAdmin: boolean };

export function VoiceFaqsView({ isAdmin }: Props) {
  const [faqs, setFaqs] = useState<VoiceFaq[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [editing, setEditing] = useState<VoiceFaq | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [category, setCategory] = useState<string>("general");
  const [triggerPhrases, setTriggerPhrases] = useState("");
  const [answerText, setAnswerText] = useState("");
  const [smsUrl, setSmsUrl] = useState("");
  const [priority, setPriority] = useState(5);

  // Test matcher
  const [testInput, setTestInput] = useState("");
  const [testResult, setTestResult] = useState<string | null>(null);

  // Regenerate state
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchFaqs();
      setFaqs(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() {
    setEditing(null);
    setCategory("general");
    setTriggerPhrases("");
    setAnswerText("");
    setSmsUrl("");
    setPriority(5);
    setFormOpen(true);
  }

  function openEdit(faq: VoiceFaq) {
    setEditing(faq);
    setCategory(faq.category);
    setTriggerPhrases(faq.trigger_phrases.join(", "));
    setAnswerText(faq.answer_text);
    setSmsUrl(faq.sms_url ?? "");
    setPriority(faq.priority);
    setFormOpen(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const phrases = triggerPhrases
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);

      if (phrases.length === 0) { setSaving(false); return; }
      if (!answerText.trim()) { setSaving(false); return; }

      const input: FaqInput = {
        category,
        trigger_phrases: phrases,
        answer_text: answerText.trim(),
        sms_url: smsUrl.trim() || null,
        priority,
      };

      if (editing) {
        await updateFaq(editing.id, input);
      } else {
        await createFaq(input);
      }

      setFormOpen(false);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(faq: VoiceFaq) {
    if (!confirm(`Disable FAQ "${faq.category}"?`)) return;
    try {
      await deleteFaq(faq.id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function handleRegenerate() {
    if (!confirm("Regenerate audio for all FAQ entries? This may take a minute.")) return;
    setRegenerating(true);
    try {
      await regenerateFaqAudio();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRegenerating(false);
    }
  }

  function runTest() {
    if (!testInput.trim()) { setTestResult(null); return; }

    const normalized = testInput.toLowerCase().replace(/[^\w\s]/g, " ").replace(/\s+/g, " ").trim();
    let best: { category: string; score: number; matched: string[] } | null = null;

    for (const faq of faqs) {
      if (!faq.enabled) continue;
      const matched: string[] = [];
      for (const phrase of faq.trigger_phrases) {
        const np = phrase.toLowerCase().replace(/[^\w\s]/g, " ").replace(/\s+/g, " ").trim();
        if (np && normalized.includes(np)) matched.push(phrase);
      }
      if (matched.length < 2) continue;
      const baseScore = matched.length / faq.trigger_phrases.length;
      const score = baseScore * (1 + faq.priority * 0.1);
      if (score < 0.3) continue;
      if (!best || score > best.score) {
        best = { category: faq.category, score, matched };
      }
    }

    if (best) {
      setTestResult(`Match: ${CATEGORY_LABELS[best.category] ?? best.category} (score: ${best.score.toFixed(2)}, matched: ${best.matched.join(", ")})`);
    } else {
      setTestResult("No match — would fall through to Claude");
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center p-8 text-tribal-muted">Loading FAQs...</div>;
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">AI FAQ Fast-Path</h2>
            <p className="text-sm text-tribal-muted">
              Pre-generated answers for common questions. Matched before Claude, ~1s response.
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleRegenerate}
                disabled={regenerating}
                className="rounded-md bg-tribal-border px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-border/80 disabled:opacity-50"
              >
                {regenerating ? "Regenerating..." : "Regenerate Audio"}
              </button>
              <button
                type="button"
                onClick={openNew}
                className="rounded-md bg-tribal-red px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-tribal-red/80"
              >
                + Add FAQ
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Test Matcher */}
        <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
          <h3 className="mb-2 text-sm font-medium text-white">Test FAQ Matcher</h3>
          <div className="flex gap-2">
            <input
              type="text"
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runTest()}
              placeholder="Type a sample caller question..."
              className="flex-1 rounded-md border border-tribal-border bg-black/30 px-3 py-1.5 text-sm text-white placeholder:text-tribal-muted"
            />
            <button
              type="button"
              onClick={runTest}
              className="rounded-md bg-tribal-border px-3 py-1.5 text-sm font-medium text-white hover:bg-tribal-border/80"
            >
              Test
            </button>
          </div>
          {testResult && (
            <p className={`mt-2 text-sm ${testResult.startsWith("Match") ? "text-green-400" : "text-yellow-400"}`}>
              {testResult}
            </p>
          )}
        </div>

        {/* FAQ Table */}
        <div className="overflow-hidden rounded-lg border border-tribal-border">
          <table className="w-full text-sm">
            <thead className="border-b border-tribal-border bg-tribal-surface">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-tribal-muted">Category</th>
                <th className="px-3 py-2 text-left font-medium text-tribal-muted">Trigger Phrases</th>
                <th className="px-3 py-2 text-left font-medium text-tribal-muted">Answer</th>
                <th className="px-3 py-2 text-center font-medium text-tribal-muted">Priority</th>
                <th className="px-3 py-2 text-center font-medium text-tribal-muted">Audio</th>
                {isAdmin && <th className="px-3 py-2 text-right font-medium text-tribal-muted">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-tribal-border">
              {faqs.map((faq) => (
                <tr
                  key={faq.id}
                  className={`transition-colors hover:bg-white/5 ${!faq.enabled ? "opacity-40" : ""}`}
                >
                  <td className="px-3 py-2 font-medium text-white">
                    {CATEGORY_LABELS[faq.category] ?? faq.category}
                  </td>
                  <td className="max-w-[200px] truncate px-3 py-2 text-tribal-muted">
                    {faq.trigger_phrases.join(", ")}
                  </td>
                  <td className="max-w-[250px] truncate px-3 py-2 text-tribal-muted">
                    {faq.answer_text}
                  </td>
                  <td className="px-3 py-2 text-center text-tribal-muted">{faq.priority}</td>
                  <td className="px-3 py-2 text-center">
                    {faq.audio_key ? (
                      <span className="text-green-400">&#10003;</span>
                    ) : (
                      <span className="text-yellow-400">&#8226;</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => openEdit(faq)}
                        className="mr-2 text-tribal-muted hover:text-white"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(faq)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Disable
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {faqs.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 6 : 5} className="px-3 py-8 text-center text-tribal-muted">
                    No FAQ entries yet. Add one to enable instant answers.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Add/Edit Form Modal */}
        {formOpen && isAdmin && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="w-full max-w-lg rounded-lg border border-tribal-border bg-tribal-bg p-6">
              <h3 className="mb-4 text-lg font-semibold text-white">
                {editing ? "Edit FAQ" : "New FAQ"}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-tribal-muted">Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded-md border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-tribal-muted">
                    Trigger Phrases <span className="text-tribal-muted">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={triggerPhrases}
                    onChange={(e) => setTriggerPhrases(e.target.value)}
                    placeholder="hours, open, close, what time, when do you"
                    className="w-full rounded-md border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white placeholder:text-tribal-muted"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-tribal-muted">Answer Text</label>
                  <textarea
                    value={answerText}
                    onChange={(e) => setAnswerText(e.target.value)}
                    rows={3}
                    placeholder="The spoken response text that will be pre-generated as audio..."
                    className="w-full rounded-md border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white placeholder:text-tribal-muted"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-tribal-muted">
                    SMS URL <span className="text-tribal-muted">(optional, sent if caller is mobile)</span>
                  </label>
                  <input
                    type="text"
                    value={smsUrl}
                    onChange={(e) => setSmsUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full rounded-md border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white placeholder:text-tribal-muted"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-tribal-muted">
                    Priority <span className="text-tribal-muted">(0-100, higher wins ties)</span>
                  </label>
                  <input
                    type="number"
                    value={priority}
                    onChange={(e) => setPriority(Number(e.target.value))}
                    min={0}
                    max={100}
                    className="w-24 rounded-md border border-tribal-border bg-black/30 px-3 py-2 text-sm text-white"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setFormOpen(false)}
                  className="rounded-md bg-tribal-border px-4 py-2 text-sm font-medium text-white hover:bg-tribal-border/80"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded-md bg-tribal-red px-4 py-2 text-sm font-medium text-white hover:bg-tribal-red/80 disabled:opacity-50"
                >
                  {saving ? "Saving..." : editing ? "Update" : "Create"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
